import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  selectIsPersonalFile,
  selectPreviewedFile,
} from 'features/content-library/contentLibrarySlice';
import { contentLibraryFileOpenRequested } from 'features/content-library/actions';
import { canOpenContentLibraryItem } from 'features/content-library/utils/canOpenContentLibraryItem';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import { GridPanelControl } from 'features/layout/grid-panel/GridPanelControl';

export const useContentPreviewModeToggle = () => {
  const { t } = useTranslation('room');
  const dispatch = useAppDispatch();
  const previewedFile = useAppSelector(selectPreviewedFile);
  const isPersonalFile = useAppSelector((state) =>
    previewedFile ? selectIsPersonalFile(state, previewedFile.id) : false
  );

  const { hasPermissions } = usePermissions();

  const controlEnabled =
    !!previewedFile &&
    canOpenContentLibraryItem(previewedFile.type, {
      canPresentLibrary: hasPermissions(PermissionTypes.presentContentLibrary),
      canEditWhiteboard: hasPermissions(PermissionTypes.editWhiteboard),
    });

  const handleClick = () => {
    if (!controlEnabled) {
      return;
    }
    dispatch(contentLibraryFileOpenRequested({ id: previewedFile.id, personal: isPersonalFile }));
  };

  return {
    controlEnabled,
    handleClick,
    label: t('grid.preview_mode'),
    tooltip: t('grid.preview_mode_tooltip'),
  };
};

export const ContentPreviewModeToggle = () => {
  const { controlEnabled, handleClick, label, tooltip } = useContentPreviewModeToggle();

  if (!controlEnabled) {
    return null;
  }

  return (
    <GridPanelControl
      icon="eye"
      tooltip={tooltip}
      onClick={handleClick}
      text={label}
      sx={{
        width: 'auto',
        px: 3,
        borderRadius: '20px',
        color: 'white',
        backgroundColor: 'primary.main',
        borderColor: 'primary.main',
        whiteSpace: 'nowrap',
        '&:after': { borderRadius: '12px' },
        '&:hover': { backgroundColor: 'primary.main' },
      }}
    />
  );
};
